import { CheckCircleFilled, ClockCircleOutlined, SaveOutlined } from '@ant-design/icons'
import { Alert, Button, Result, Skeleton, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import {
  getVirtualIpDraft,
  getVirtualIpQuestionnaire,
  saveVirtualIpDraft,
  submitVirtualIpStep1,
  type SaasApiError,
  type VirtualIpAnswers,
  type VirtualIpDraft,
  type VirtualIpQuestionnaire,
} from '../../api'
import { answeredCount, missingRequiredQuestions } from './answerPredicates'
import QuestionCard from './QuestionCard'
import VirtualIpStepper from './VirtualIpStepper'
import { VIRTUAL_IP_FLOW_TOTAL_STEPS } from './virtualIpFlow'
import { useResolvedVirtualIpProgress } from './useResolvedVirtualIpProgress'
import './virtualIp.css'

type PageState = 'loading' | 'empty_config' | 'ready' | 'submitting' | 'step_1_completed'
type SaveState = 'idle' | 'saving' | 'saved' | 'error'

const AUTOSAVE_DELAY_MS = 900

export default function VirtualIpStep1Page() {
  const { t } = useTranslation(['virtualIp', 'actions'])
  const { workspaceId = '', projectId = '' } = useParams()
  const navigate = useNavigate()

  const [questionnaire, setQuestionnaire] = useState<VirtualIpQuestionnaire | null>(null)
  const [, setDraft] = useState<VirtualIpDraft | null>(null)
  const [answers, setAnswers] = useState<VirtualIpAnswers>({})
  const [pageState, setPageState] = useState<PageState>('loading')
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [showMissingOnly, setShowMissingOnly] = useState(false)

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const latestAnswersRef = useRef<VirtualIpAnswers>({})
  const progress = useResolvedVirtualIpProgress()

  useEffect(() => {
    if (!workspaceId || !projectId) return
    let cancelled = false
    setPageState('loading')
    setLoadError(null)
    Promise.all([getVirtualIpQuestionnaire(workspaceId), getVirtualIpDraft(workspaceId, projectId)])
      .then(([qRes, dRes]) => {
        if (cancelled) return
        setQuestionnaire(qRes.data)
        setDraft(dRes.data)
        setAnswers(dRes.data.answers || {})
        latestAnswersRef.current = dRes.data.answers || {}
        if (qRes.data.questions.length === 0) {
          setPageState('empty_config')
        } else if (dRes.data.status === 'step_1_completed') {
          setPageState('step_1_completed')
        } else {
          setPageState('ready')
        }
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError((e as SaasApiError).message || t('step1.loadQuestionnaireFailed'))
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, projectId, t])

  const persistDraft = useCallback(
    (nextAnswers: VirtualIpAnswers) => {
      if (!workspaceId || !projectId) return
      setSaveState('saving')
      saveVirtualIpDraft(workspaceId, nextAnswers, 1, projectId)
        .then((r) => {
          setDraft(r.data)
          setSaveState('saved')
        })
        .catch(() => setSaveState('error'))
    },
    [workspaceId],
  )

  const scheduleAutosave = useCallback(
    (nextAnswers: VirtualIpAnswers) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => persistDraft(nextAnswers), AUTOSAVE_DELAY_MS)
    },
    [persistDraft],
  )

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
    },
    [],
  )

  const handleAnswerChange = useCallback(
    (key: string, value: unknown) => {
      setAnswers((prev) => {
        const next = { ...prev, [key]: value }
        latestAnswersRef.current = next
        scheduleAutosave(next)
        return next
      })
      setValidationError(null)
    },
    [scheduleAutosave],
  )

  const handleManualSave = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    persistDraft(latestAnswersRef.current)
    message.open({ type: 'loading', content: t('common.savingDraft'), key: 'vip-save', duration: 0 })
    saveVirtualIpDraft(workspaceId, latestAnswersRef.current, 1, projectId)
      .then((r) => {
        setDraft(r.data)
        setSaveState('saved')
        message.success({ content: t('common.draftSaved'), key: 'vip-save', duration: 1.5 })
      })
      .catch((e) => {
        setSaveState('error')
        message.error({ content: (e as SaasApiError).message || t('common.saveFailed'), key: 'vip-save', duration: 2 })
      })
  }

  const questions = questionnaire?.questions ?? []
  const completed = useMemo(() => answeredCount(questions, answers), [questions, answers])
  const missing = useMemo(() => missingRequiredQuestions(questions, answers), [questions, answers])
  const missingKeys = useMemo(() => new Set(missing.map((q) => q.key)), [missing])

  const handleSubmit = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    if (missing.length > 0) {
      setShowMissingOnly(true)
      setValidationError(
        t('step1.missingRequired', {
          count: missing.length,
          titles: missing.map((q) => q.title).join('、'),
        }),
      )
      return
    }
    setValidationError(null)
    setPageState('submitting')
    submitVirtualIpStep1(workspaceId, latestAnswersRef.current, projectId)
      .then((r) => {
        setDraft(r.data)
        setPageState('step_1_completed')
      })
      .catch((e) => {
        setPageState('ready')
        setValidationError((e as SaasApiError).message || t('common.submitFailed'))
      })
  }

  if (loadError) {
    return (
      <div className="tc-vip-page">
        <Alert type="error" showIcon message={t('common.loadFailed')} description={loadError} />
      </div>
    )
  }

  if (pageState === 'loading') {
    return (
      <div className="tc-vip-page">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 8 }} style={{ marginTop: 20 }} />
      </div>
    )
  }

  if (pageState === 'empty_config') {
    return (
      <div className="tc-vip-page">
        <Result status="info" title={t('step1.emptyConfigTitle')} subTitle={t('step1.emptyConfigSubtitle')} />
      </div>
    )
  }

  if (pageState === 'step_1_completed') {
    return (
      <div className="tc-vip-page" data-testid="virtual-ip-step1-completed">
        <PageHeader />
        <VirtualIpStepper currentStep={1} progress={progress} />
        <Result
          icon={<CheckCircleFilled className="tc-vip-completed-icon" />}
          status="success"
          title={t('step1.savedTitle')}
          subTitle={t('step1.savedSubtitle')}
          extra={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <Button onClick={() => setPageState('ready')}>{t('step1.editAnswers')}</Button>
              <Button type="primary" onClick={() => navigate(`/workspace/${workspaceId}/virtual-ip/projects/${projectId}/step-2`)}>
                {t('step1.nextAnalyze')}
              </Button>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <div className="tc-vip-page" data-testid="virtual-ip-step1-page">
      <PageHeader />
      <VirtualIpStepper currentStep={1} progress={progress} />

      {validationError ? (
        <Alert
          type="warning"
          showIcon
          closable
          message={t('step1.completeRequired')}
          description={validationError}
          onClose={() => setValidationError(null)}
        />
      ) : null}

      <div className="tc-vip-layout">
        <div className="tc-vip-main">
          <div className="tc-vip-questions">
            {questions.map((q) => (
              <QuestionCard
                key={q.id}
                question={q}
                answers={answers}
                onAnswerChange={handleAnswerChange}
                showValidationError={showMissingOnly && missingKeys.has(q.key)}
                className={q.type === 'composite' || q.type === 'repeatable_group' || q.type === 'textarea' ? 'is-full-width' : undefined}
              />
            ))}
          </div>

          <div className="tc-data-card tc-vip-footer">
            <div className="tc-vip-footer-status">
              {saveState === 'saving' && <SaveOutlined spin />}
              {saveState === 'saving' && t('common.savingDraft')}
              {saveState === 'saved' && <CheckCircleFilled style={{ color: 'var(--success-text)' }} />}
              {saveState === 'saved' && t('common.draftSaved')}
              {saveState === 'error' && <span style={{ color: 'var(--error-text)' }}>{t('common.saveFailedNetwork')}</span>}
              {saveState === 'idle' && t('common.autoSaveHint')}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={handleManualSave}>{t('common.saveDraft')}</Button>
              <Button type="primary" loading={pageState === 'submitting'} onClick={handleSubmit}>
                {t('step1.nextSubmit')}
              </Button>
            </div>
          </div>
        </div>

        <aside className="tc-vip-aside">
          <div className="tc-data-card">
            <div className="tc-vip-card-title" style={{ marginBottom: 10 }}>{t('step1.tipsTitle')}</div>
            <ul className="tc-vip-tips-list">
              <li>{t('step1.tip1')}</li>
              <li>{t('step1.tip2')}</li>
              <li>{t('step1.tip3')}</li>
            </ul>
          </div>
          <div className="tc-data-card">
            <div className="tc-vip-card-title" style={{ marginBottom: 10 }}>{t('step1.statusTitle')}</div>
            <div>
              <span className="tc-vip-progress-value">{completed}</span>
              <span className="tc-vip-progress-value-unit">/ {questions.length}</span>
            </div>
            <div className="tc-vip-card-hint">{t('step1.questionsCompleted')}</div>
          </div>
        </aside>
      </div>
    </div>
  )
}

function PageHeader() {
  const { t } = useTranslation('virtualIp')
  const navigate = useNavigate()
  const { workspaceId = '' } = useParams()
  return (
    <div className="tc-page-header">
      <div>
        <h1 className="tc-page-title">{t('common.pageTitle')}</h1>
        <div className="tc-vip-step-label">
          <span>{t('common.stepOf', { current: 1, total: VIRTUAL_IP_FLOW_TOTAL_STEPS })}</span>
          <span className="tc-vip-step-title">{t('step1.title')}</span>
        </div>
        <p className="tc-page-desc">{t('step1.description')}</p>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        <span className="tc-vip-time-badge">
          <ClockCircleOutlined /> {t('common.estimatedTime')}
        </span>
        <Button size="small" onClick={() => navigate(`/workspace/${workspaceId}/overview`)}>
          {t('common.backToOverview')}
        </Button>
      </div>
    </div>
  )
}
